/**
 * @module app.um
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { AuthService } from 'app/um/services/auth-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { getReference } from 'app/shared/models/essette-types';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import { pushCopy, spliceCopy } from 'app/shared/lib/utilities';

@autoinject()
export class AddFlaggedServices extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected authFlagId: string | null = null;

    protected selectedServices: CD.Service[] = [];

    protected serviceSearchQuery: string | null = null;   // the value of the autocomplete text box
    protected serviceSearchResults: CD.Service[] = [];  // array of search results from API for autocomplete
    protected currentServices: CD.Service[] = [];        // array of current services from API

    ////****************** Element References ***************************************////
    protected $autocomplete!: EssAutocompleteCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload()
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAuthFlagServices();
    }

    /** Called when a user clicks on an auto-complete search result */
    protected addService(service: CD.Service): void {
        this.selectedServices = pushCopy(this.selectedServices, service);
        this.serviceSearchQuery = '';
        this.serviceSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected removeSelectedService(service: CD.Service): void {
        this.selectedServices = spliceCopy(this.selectedServices, this.selectedServices.indexOf(service, 0), 1);
        if(this.selectedServices.length === 0) this.$autocomplete.clear();
    }

    protected filterServices = (service: CD.Service): boolean => {
        if(this.selectedServices.some(serv => service.id === serv.id)) {
            return false;
        }
        if(!this.currentServices && !service) { return false; }
        return !this.currentServices.some(serv => service.id === serv.id);
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.addSelectedServices();
    }

    public reset(): void {
        this.selectedServices.splice(0);
        this.serviceSearchQuery = '';
        this.serviceSearchResults.splice(0);
        this.$autocomplete.clear();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthFlagServices(): Promise<void> {
        if(!this.authFlagId) return;

        try {
            this.state.beginLoad();

            const response = await this.service.loadServicesByAuthFlag(this.authFlagId, { all: true });
            this.currentServices = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    /** Called as user types into the auto-complete service search box */
    @apiCall
    protected async searchServices(value: string): Promise<void> {
        if(!value) {
            if(this.serviceSearchResults) this.serviceSearchResults.splice(0);
            return;
        }

        const options: Partial<IQueryLoadOptions> = { top: 30 };

        try {
            this.state.beginLoad();
            const serviceSearchFilters: IServiceSearchFilters = { codeOrDescription: value };
            const response = await this.lookupService.searchServices(undefined, options, [], true, serviceSearchFilters);
            this.serviceSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async addSelectedServices(): Promise<boolean> {
        if(!this.authFlagId) return false;
        const authFlagToInsert: UM.AuthFlag = {
            id: this.authFlagId,
            services: this.selectedServices.map(s => getReference(s)!)
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.service.insertAuthFlagDetails(authFlagToInsert);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.added('um:service.$', isSuccessful, this.selectedServices.length);
            this.state.endSave();
        }
    }
}
